import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { notificationsService } from '@/services/apiClient';
import type {
  CreateNotificationData,
  CreateNotificationResponse,
  MarkAllAsReadResponse,
  MarkAsReadData,
  Notification,
  NotificationsQuery,
  NotificationsResponse,
  SendEmailData,
  SendEmailResponse,
  SendPushNotificationData,
  SendPushNotificationResponse,
} from '@/types/notifications';

export const notificationsKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationsKeys.all, 'list'] as const,
  list: (query: NotificationsQuery) => [...notificationsKeys.lists(), query] as const,
  details: () => [...notificationsKeys.all, 'detail'] as const,
  detail: (id: string) => [...notificationsKeys.details(), id] as const,
};

export const useNotifications = (query: NotificationsQuery = {}) => {
  return useQuery<NotificationsResponse, Error>({
    queryKey: notificationsKeys.list(query),
    queryFn: () => notificationsService.getNotifications(query),
    staleTime: 30 * 1000, // 30 seconds
  });
};

export const useNotification = (notificationId: string) => {
  return useQuery<Notification, Error>({
    queryKey: notificationsKeys.detail(notificationId),
    queryFn: () => notificationsService.getNotification(notificationId),
    enabled: !!notificationId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateNotification = () => {
  const queryClient = useQueryClient();
  return useMutation<CreateNotificationResponse, Error, CreateNotificationData>({
    mutationFn: (data: CreateNotificationData) => notificationsService.createNotification(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsKeys.lists() });
    },
  });
};

export const useMarkAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation<Notification, Error, { notificationId: string; data: MarkAsReadData }>({
    mutationFn: ({ notificationId, data }) => notificationsService.markAsRead(notificationId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: notificationsKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: notificationsKeys.detail(variables.notificationId),
      });
    },
  });
};

export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation<MarkAllAsReadResponse, Error>({
    mutationFn: () => notificationsService.markAllAsRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
};

export const useDeleteNotification = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (notificationId: string) => notificationsService.deleteNotification(notificationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsKeys.lists() });
    },
  });
};

export const useSendPushNotification = () => {
  return useMutation<SendPushNotificationResponse, Error, SendPushNotificationData>({
    mutationFn: (data: SendPushNotificationData) =>
      notificationsService.sendPushNotification(data),
  });
};

export const useSendEmail = () => {
  return useMutation<SendEmailResponse, Error, SendEmailData>({
    mutationFn: (data: SendEmailData) => notificationsService.sendEmail(data),
  });
};

